import React, {Fragment, useEffect,} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {
    moderatorModeSelector,
    currentUserInfoSelector,
    isSessionLoadedSelector
} from "../../../../auth/store/auth-user/selectors";
import {
    setScanCongratulationsVisibility, stickerShowCongratulation
} from "../../../store/sticker-show/actions";
import {themesSelector} from '../../../store/dictionaries/selectors';
import StickersComponentsScannerHeader from "../../components/scanner-header";
import Div100vh from "../../../../uq-ui/components/div-100";
import styles from "./style.css";
import StickersComponentsCongratulations from "../../components/congratulations";
import {Grid} from "../../components/grid";
import {Route, useHistory, useParams} from "react-router";
import {
    stickerItemSelector,
} from "../../../store/entities/sticker-items/selectors";
import {stickerShowCongratulationSelector} from "../../../store/sticker-show/selectors";
import {stickerFetchStatusSelector} from "../../../store/entities/stickers/selectors";
import {stickerItemsFetchStatusSelector} from "../../../store/entities/sticker-items/selectors";
import {GridItem} from "../../components/grid-item";
import {formatAuthorName} from "./helpers";
import {formatDateTime} from "../../../../uq-ui/helpers/datetimeFormatter";
import {StickerFormatEnum} from "../../../domain/enums/StickerFormatEnum";
import Icon from "../../../../icons/Icon";
import UQButton from "../../../../uq-ui/components/button";
import LayoutComponentsLoaderProcessing from "../../../../layout/ui/components/loader-processing";
import ErrorScreen from "../../../../layout/ui/screens/error";
import {useSticker} from "../../../store/sticker-show/hooks";
import { isMemorialType } from "../../../store/sticker-show/selectors";
import _ from "lodash";
import UiIcon from "../../../../uq-ui/components/ui-icon";
import ModeratorCog from "../../components/moderator-cog";

export function StickerItemGridItem({stickerItemId}: { stickerItemId: string }) {
    const stickerItem = useSelector((state) => stickerItemSelector(state, stickerItemId))
    const themes = useSelector(themesSelector);
    const history = useHistory();

    if (!stickerItem || !stickerItem.configured_date) {
        return <></>
    }
    const name = formatAuthorName(stickerItem.from?.display_name)
    let date = formatDateTime(stickerItem.configured_date);
    if (date) {
        date = date.split(',')[0]
    }
    let url;
    switch (stickerItem.format) {
        case StickerFormatEnum.Audio:
            url = themes.find(x => x.id === stickerItem.template)?.in_list?.source?.src
            break;
        case StickerFormatEnum.Image:
            url = stickerItem.photo?.thumbnail?.source;
            break;
        case StickerFormatEnum.Video:
            url = stickerItem.video?.thumbnail?.source;
            break;

    }

    return (
        <GridItem
            name={name}
            date={date}
            background={[url]}
            id={stickerItemId}
            onClick={() => history.push(`/stickers/${stickerItem.sticker_id}/${stickerItem.id}`)}/>
    );
}

export function StickerItemPreviewItem({stickerItemId}: { stickerItemId: string }) {
    const stickerItem = useSelector((state) => stickerItemSelector(state, stickerItemId))
    const themes = useSelector(themesSelector);
    const history = useHistory();

    if (!stickerItem || !stickerItem.configured_date) {
        return <></>
    }
    let date = formatDateTime(stickerItem.configured_date);
    if (date) {
        date = date.split(',')[0]
    }
    let url;
    switch (stickerItem.format) {
        case StickerFormatEnum.Audio:
            url = themes.find(x => x.id === stickerItem.template)?.in_list?.source?.src
            break;
        case StickerFormatEnum.Image:
            url = stickerItem.photo?.thumbnail?.source;
            break;
        case StickerFormatEnum.Video:
            url = stickerItem.video?.thumbnail?.source;
            break;

    }

    return (
        <div className={styles.stickerItemsPreviewItem} style={{ backgroundImage: 'url("' + url + '")' }} onClick={() => history.push(`/stickers/${stickerItem.sticker_id}/${stickerItem.id}`)}></div>
    );
}


export default function StickerScreensShow(props) {
    const {id: stickerId}: { id: string } = useParams();
    const {t} = useTranslation("scr-show-sticker");
    const stickerScanEvent = useSelector(stickerShowCongratulationSelector);
    const currentUser = useSelector(currentUserInfoSelector);
    const history = useHistory();
    const dispatch = useDispatch();
    const [sticker, stickerItems] = useSticker(stickerId);
    const stickerStatus = useSelector((state) => stickerFetchStatusSelector(state, stickerId));
    const stickerItemsStatus = useSelector((state) => stickerItemsFetchStatusSelector(state, stickerId));
    const isSessionLoaded = useSelector(isSessionLoadedSelector);
    const isStickerTypeMemorial = useSelector(isMemorialType);
    // The cog shows only while moderator mode (sidebar toggle) is on.
    const canModerate = useSelector(moderatorModeSelector);

    const canAddStickerItem =
        (currentUser.id === sticker?.from || !sticker?.from)
        && sticker?.sticker_items_limit
        && stickerItems
        && (stickerItems.length < sticker.sticker_items_limit);

    useEffect(() => {
        // Only a successfully loaded sticker may enter the create flow. The
        // items request succeeds with [] even when the sticker request failed,
        // which used to fabricate a phantom sticker entity here and bounce
        // not-found stickers into /new (and from there to the error screen).
        if (stickerStatus === 'succeeded' && stickerItemsStatus === 'succeeded'
            && stickerId && sticker && !sticker.from && stickerItems && stickerItems.length === 0) {
            history.push(`/stickers/${stickerId}/new`);
        }
    }, [stickerStatus, stickerItemsStatus, stickerId, sticker, stickerItems])

    // The visit flow: a loader while the sticker is fetched, the sticker once
    // it loads, and the app's default error screen when the server says it
    // does not exist. Rendered through Route so the screen gets match/history
    // and its "try again" button can re-enter /:id.
    if (stickerStatus === 'failed') {
        return <Route component={ErrorScreen}/>;
    }
    // One uninterrupted loader until the verdict is in: the sticker has
    // resolved AND its items are KNOWN (sticker.stickerItems set by the first
    // items response). Deliberately not gated on the transient fetch status:
    // item child components re-dispatch the items load when they mount, and a
    // status gate would unmount them back to the loader on every refetch -
    // an infinite mount/fetch loop (seen as an endless /items request storm
    // on direct /stickers/:id URLs).
    if (!sticker || (stickerItems == null && stickerItemsStatus !== 'failed')) {
        return <LayoutComponentsLoaderProcessing show={true}/>;
    }

    const showCongratulation = stickerScanEvent && stickerItems && stickerItems.length > 0;
    if (showCongratulation) {
        return (isSessionLoaded
            &&
          <Fragment>
            <StickersComponentsCongratulations
              // Congratulations only shows for an already-configured sticker: no
              // format picker here, even when more uploads are allowed - adding
              // happens inside the sticker page with its Add button.
              canAddStickerItem={false}
              containerStyle={{zIndex: 1101}}
              title={t('congratulationsTitle')}
              viewItPressed={() => {
                  if (sticker?.sticker_items_limit == 1 && stickerItems?.length == sticker?.sticker_items_limit) {
                      history.push(`/stickers/${stickerId}/${stickerItems?.[0]?.id}`)
                  }
                  dispatch(setScanCongratulationsVisibility(false));
                  dispatch(stickerShowCongratulation(false));
              }}
              showAnimation={sticker?.type === "GREETING"}
            />
          </Fragment>
        )
    }

    const onAddItemClick = () => {
        history.push(`/stickers/${stickerId}/new`)
    }

    return <Fragment>
        {/*
             // @ts-ignore */}
        <StickersComponentsScannerHeader theme="brand"
                                         title={t(isStickerTypeMemorial ? "headerTitleMemorial" : "headerTitle")}
                                         showLogo={true}
                                         showBackBtn={false}
        />
        <Div100vh className={styles.scrollView}>
            <div className={styles.header}>
                <div className={`${styles.title} font-title`}
                     dangerouslySetInnerHTML={{__html: t(isStickerTypeMemorial ? "titleMemorial" : "title")}}/>
                <div className={styles.stickerItemsPreview}>
                    {
                        sticker?.stickerItems
                        && sticker.stickerItems.map(id => <StickerItemPreviewItem key={id} stickerItemId={id}/>)
                    }
                </div>
            </div>
            <div className={styles.stickerInfo}>
                <div className={styles.scansCounterContainer}>
                    <UiIcon name="eye" size={16} color="var(--c-text-muted)" className={styles.scansCounterIcon}/>
                    <div
                        className={styles.scansCounterText}>{` ${sticker?.scans_count || '0'} ${t('views')}`}</div>
                </div>
                {canAddStickerItem &&
                <UQButton label={''}
                          ariaLabel={t('addButton')}
                          justIcon={true}
                          theme="brand"
                          containerClass={styles.addButton}
                          icon={<UiIcon name="plus" size={26} strokeWidth={2.4} color="var(--c-on-accent)"/>}
                          onPress={onAddItemClick}/>}
            </div>
            <Grid>
                {
                    sticker?.stickerItems
                    && sticker.stickerItems.map(id => <StickerItemGridItem key={id} stickerItemId={id}/>)
                }
            </Grid>
            {/* Room under the last memory so the floating cog never covers it. */}
            {canModerate && <div className={styles.cogSpacer}/>}
        </Div100vh>
        {canModerate && <ModeratorCog stickerId={stickerId} type={sticker?.type}/>}
    </Fragment>
}


